import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { Align, Collision } from '@progress/kendo-angular-popup';
import { SubmittalStatus, SubmittalSubStatus } from '../../models/submittal-status.model';

@Component({
    selector: 'nova-submittal-status-filter',
    templateUrl: './submittal-status-filter.component.html',
    styleUrls: ['./submittal-status-filter.component.scss']
})
export class SubmittalStatusFilterComponent {
    @ViewChild('popupAnchor', { static: true })
    public popupAnchor: ElementRef;

    @Input()
    statuses: SubmittalStatus[] = [];

    @Output() filterChanged = new EventEmitter();
    @Output() opened = new EventEmitter();

    isOpened: boolean;
    anchorAlign: Align = { horizontal: 'left', vertical: 'bottom' };
    popupAlign: Align = { horizontal: 'left', vertical: 'top' };
    collision: Collision = { horizontal: 'flip', vertical: 'fit' };
    selectionUpdated = false;
    spaceRegex = / /g;

    constructor() {}

    get allSelected(): boolean {
        return this.statuses.every((t) => t.selected === true);
    }

    get selectedStatuses(): number[] {
        return this.statuses.filter((t) => t.selected === true).map((t) => t.id);
    }

    get selectedSubStatuses(): number[] {
        return this.statuses
            .filter((t) => t.selected === true && t.subStatuses)
            .flatMap((t) => t.subStatuses)
            .filter((t) => t && t.selected === true)
            .map((t) => t.id);
    }

    get selectionText(): string {
        const a = this.selectedStatuses.length;
        const b = this.selectedSubStatuses.length;

        return a > 0 || b > 0 ? `${(a + b).toString()} selected` : null;
    }

    @HostListener('document:click', ['$event'])
    public documentClick(event: any): void {
        if (!this.popupAnchor.nativeElement.contains(event.target) && !event.target.closest('kendo-calendar')) {
            this.close();
        }
    }

    @HostListener('keydown', ['$event'])
    public keydown(event: any): void {
        if (event.keyCode === 27) {
            this.close();
        }
    }

    updateSubStatus(status: SubmittalStatus, subStatus: SubmittalSubStatus, checked: boolean) {
        const list = this.statuses.map((t) => {
            const subList = t.subStatuses?.map((s) => {
                return s.id === subStatus.id ? { ...s, selected: checked } : s;
            });

            const selectionCount = subList?.filter((x) => x.selected === true).length;

            return t.id === status.id
                ? {
                      ...t,
                      subStatuses: subList,
                      selected: subList.findIndex((x) => x.selected === true) !== -1,
                      indeterminate: selectionCount > 0 && selectionCount !== subList.length
                  }
                : t;
        });
        this.statuses = list;
        this.selectionUpdated = true;
    }

    updateStatus(status: SubmittalStatus, checked: boolean) {
        const list = this.statuses.map((t) => {
            return t.id === status.id
                ? {
                      ...t,
                      subStatuses: t.subStatuses?.map((s) => ({ ...s, selected: checked })),
                      selected: checked,
                      indeterminate: false
                  }
                : t;
        });
        this.statuses = list;
        this.selectionUpdated = true;
    }

    clear() {
        this.selectAll(false);
        this.close();
    }

    selectAll(flag: boolean) {
        const list = this.statuses.map((t) => ({
            ...t,
            subStatuses: t.subStatuses?.map((s) => ({ ...s, selected: flag })),
            selected: flag,
            indeterminate: false
        }));
        this.statuses = list;
        this.selectionUpdated = true;
    }

    popupOpened(): void {
        this.selectionUpdated = false;
    }

    toggleFilter(): void {
        this.isOpened = !this.isOpened;
    }

    close(): void {
        this.isOpened = false;
        if (this.selectionUpdated) {
            this.filterChanged.emit({
                statuses: this.statuses
                              .filter((t) => t.selected === true && ( t.indeterminate === null || t.indeterminate === false))
                              .map((t) => t.id),
                subStatuses: this.selectedSubStatuses,
                list: this.statuses
            });
            this.selectionUpdated = false;
        }
    }

    popupClicked($event): void {
        $event.stopPropagation();
    }
}
